import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBundleDetails,
  fetchBundleList,
  getEntitySuspensionDetails,
  resetCustomerSuspensionChanges,
} from '../actions/orderActions';
import { CustomerSuspensionSource } from '../model/customerSuspensionDetails';
import OrderListItem from './orderListItem';
import SuspensionPopover from './suspensionPopover';
import Unsuspension from './unsuspension';
import { ENTITY_TYPE, isRoleTitan, ORDER } from './utils';

const OrderList = () => {
  // Local state for the unsuspension modal
  const [showCustomerSuspensionModal, setShowCustomerSuspensionModal] =
    useState(false);
  const dispatch = useDispatch();

  // Grab the required state slices from Redux
  const {
    // domainOrders,
    // customerOrders,
    domainBundles,
    customerBundles,
    currentDomainName: domainName,
    currentEmail: email,
    authToken,
    userRole,
    userPartners,
    isCustomerSuspendedOrderList,
  } = useSelector((state) => ({
    // domainOrders: state.orders.domainOrders,
    // customerOrders: state.orders.customerOrders,
    domainBundles: state.orders.domainBundles,
    customerBundles: state.orders.customerBundles,
    currentDomainName: state.orders.currentDomainName,
    currentEmail: state.orders.currentEmail,
    authToken: state.auth.token,
    userRole: state.auth.userRole,
    userPartners: state.auth.userPartners,
    query: state.orders.queryText,
    isCustomerSuspendedOrderList: state.orders.isCustomerSuspendedOrderList,
  }));

  const accessToMultiplePartners = userPartners && userPartners.length > 1;

  // Called when an order is clicked. It figures out the order details and dispatches the fetchOrderDetails action.
  const handleOrderClick = (index, isCustomerBundle = false) => {
    const bundles = isCustomerBundle ? customerBundles : domainBundles;
    const entityId = bundles[index]?.customer?.id || null;
    const { id } = bundles[index];
    const bundleDetails = dispatch(fetchBundleDetails(id, authToken));
    dispatch(
      getEntitySuspensionDetails(
        [entityId],
        ENTITY_TYPE.CUSTOMER.toUpperCase(),
        authToken
      )
    );

    return bundleDetails;
  };

  // Opens the unsuspension modal
  const handleUnsuspendClick = () => {
    setShowCustomerSuspensionModal(true);
  };

  // Closes the modal and triggers additional actions based on email presence
  const handleModalClose = (emailParam) => {
    if (emailParam) {
      // dispatch(fetchOrderList(emailParam, authToken, false));
      dispatch(fetchBundleList(emailParam, authToken, false));
    } else {
      dispatch(resetCustomerSuspensionChanges());
    }
    setShowCustomerSuspensionModal(false);
  };

  // Gets customer IDs from customer orders
  // const getCustomerIds = () => getUserIdsFromCustomerOrders(customerOrders);
  const getCustomerIds = () => {};

  // Build header arrays based on user role and partner access
  const getHeaders = (orderType) => {
    const partnerHeader = {
      condition: isRoleTitan(userRole) || accessToMultiplePartners,
      id: 'order-list-header-partner',
      className: 'order-column-partner',
    };

    // For domain orders we show "source", for customer orders "domain-name"
    const sourceHeader = {
      id:
        orderType === ORDER.DOMAIN
          ? 'order-list-header-source'
          : 'order-list-header-domain-name',
      className: 'order-column-source',
    };

    return [
      partnerHeader,
      sourceHeader,
      { id: 'order-list-header-date', className: 'order-column-creation-date' },
      { id: 'order-list-header-status', className: 'order-column-status' },
    ];
  };

  const domainBundlesListHeaders = getHeaders(ORDER.DOMAIN);
  const customerOrderHeaders = getHeaders(ORDER.CUSTOMER);

  const domainHeaders = domainBundlesListHeaders
    .filter((header) => header.condition === undefined || header.condition)
    .map((header) => (
      <th key={header.id} className={`${header.className} order-column-header`}>
        <FormattedMessage id={header.id} />
      </th>
    ));

  const customerHeaders = customerOrderHeaders
    .filter((header) => header.condition === undefined || header.condition)
    .map((header) => (
      <th key={header.id} className={`${header.className} order-column-header`}>
        <FormattedMessage id={header.id} />
      </th>
    ));

  const domainBundleListHeader = (
    <tr className="order-list-row order-list-row-header">{domainHeaders}</tr>
  );

  const customerBundleListHeader = (
    <tr className="order-list-row order-list-row-header">{customerHeaders}</tr>
  );

  const domainOrderListItemComponent = domainBundles.map((order, index) => (
    <OrderListItem
      key={`domain-${index}`}
      order={order}
      index={index}
      orderType={ORDER.DOMAIN}
      userRole={userRole}
      accessToMultiplePartners={accessToMultiplePartners}
      onViewClicked={(index) => handleOrderClick(index)}
      isLastOrderInList={index === domainBundles.length - 1}
    />
  ));

  const customerOrderListItemComponent = customerBundles
    ? customerBundles.map((order, index) => (
        <OrderListItem
          key={`customer-${index}`}
          order={order}
          index={index}
          orderType={ORDER.CUSTOMER}
          userRole={userRole}
          accessToMultiplePartners={accessToMultiplePartners}
          onViewClicked={(index) => handleOrderClick(index, true)}
        />
      ))
    : [];

  return (
    <div className="orders-response">
      {domainBundles && domainBundles.length > 0 && (
        <>
          <div className="domain-order-for">
            <span>
              <FormattedMessage id="order-list-domain" />
            </span>
            <span className="domain-name">{domainName}</span>
          </div>
          <table className="order-list-box">
            <thead>{domainBundleListHeader}</thead>
            <tbody>{domainOrderListItemComponent}</tbody>
          </table>
        </>
      )}

      {customerBundles && customerBundles.length > 0 && (
        <>
          <div className="domain-order-for">
            <span>
              <FormattedMessage id="order-list-customer" />
            </span>
            <span className="domain-name">{email}</span>
            {isCustomerSuspendedOrderList && (
              <div className="order-list-suspension-container">
                <div className="order-list-suspension-separator" />
                <span className="suspended-text">Suspended</span>
                <SuspensionPopover
                  entityType={ENTITY_TYPE.CUSTOMER}
                  customerEmail={email}
                  customerIds={getCustomerIds()}
                  shouldShowUnsuspendOption={isRoleTitan(userRole)}
                  handleUnsuspendClick={handleUnsuspendClick}
                  hoverIconCss="order-list-suspension-icon"
                />
              </div>
            )}
          </div>
          <table className="order-list-box">
            <thead>{customerBundleListHeader}</thead>
            <tbody>{customerOrderListItemComponent}</tbody>
          </table>
        </>
      )}

      {showCustomerSuspensionModal && (
        <div className="modal">
          <div className="modal-content unsuspension-modal">
            <Unsuspension
              suspensionType={ENTITY_TYPE.CUSTOMER}
              source={CustomerSuspensionSource.OrderList}
              email={email}
              closeModal={handleModalClose}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList;
